import type { User, AuthState, AuthActions } from './types';
import { useKeycloak } from './KeycloakProvider';

const getUserRoles = (tokenParsed: any, authenticated?: boolean): string[] => {
  const roles = tokenParsed?.resource_access?.['nextjs-frontend']?.roles || [];
  if (!authenticated || roles.some((role: string) => role?.toUpperCase?.() === 'USER')) {
    return roles;
  }

  return [...roles, 'USER'];
};

/**
 * Hook to access authentication state and actions
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isAuthenticated, login, logout } = useAuth();
 *   
 *   if (!isAuthenticated) {
 *     return <button onClick={() => login()}>Login</button>;
 *   }
 *   
 *   return (
 *     <div>
 *       <p>Welcome, {user?.firstName}!</p>
 *       <button onClick={() => logout()}>Logout</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth(): AuthState & AuthActions {
  const { keycloak, initialized } = useKeycloak();

  const getRedirectUri = (redirectUri?: string): string => {
    if (redirectUri) {
      return redirectUri.startsWith('http')
        ? redirectUri
        : `${window.location.origin}${redirectUri.startsWith('/') ? '' : '/'}${redirectUri}`;
    }

    return `${window.location.origin}${window.location.pathname}`;
  };

  // Extract user from token
  const user: User | null = keycloak.tokenParsed
    ? {
        id: keycloak.tokenParsed.sub!,
        username: keycloak.tokenParsed.preferred_username!,
        email: keycloak.tokenParsed.email!,
        firstName: keycloak.tokenParsed.given_name,
        lastName: keycloak.tokenParsed.family_name,
        emailVerified: keycloak.tokenParsed.email_verified,
        roles: getUserRoles(keycloak.tokenParsed, keycloak.authenticated),
      }
    : null;

  return {
    // State
    user,
    isAuthenticated: keycloak.authenticated ?? false,
    isLoading: !initialized,
    token: keycloak.token,
    refreshToken: keycloak.refreshToken,

    // Actions
    login: (redirectUri?: string) => {
      return keycloak.login({ redirectUri: getRedirectUri(redirectUri) });
    },
    logout: () => keycloak.logout(),
    register: (redirectUri?: string) => {
      return keycloak.register({ redirectUri: getRedirectUri(redirectUri) });
    },
    updateToken: (minValidity?: number) => keycloak.updateToken(minValidity),
  };
}
